import { Component, computed, effect, input, signal } from '@angular/core';
import { GraphApiService, StaffApiService, SupervisorApiService } from '../../core/api/services';
import { SelectItem, SelectItemGroup } from 'primeng/api';
import { Router } from '@angular/router';
import { GraphAnnotationModel, GraphAnnotationType, PatientBehaviorModel, PatientProgramModel, PatientTargetModel, SkillAreaModel, UserModel } from '../../core/api/models';
import { MultiSelectChangeEvent } from 'primeng/multiselect';
import { forkJoin } from 'rxjs';
import { AlertService } from '../../shared/services/alert.service';
import { AccountApiService } from '../../core/api/services/account-api.service';
import { Permissions } from '../../core/api/models/permissions';
import { PatientDataFacadeService } from '../../core/services/patient-data-facade.service';

@Component({
  selector: 'app-annotations',
  templateUrl: './annotations.component.html',
  styleUrl: './annotations.component.css',
  standalone: false,
})
export class AnnotationsComponent {
  patientID = input.required<string>();
  patientIDAsNumber: number | null = null;

  user = signal<UserModel | null>(null);

  isLoading = false;

  annotations = signal<GraphAnnotationModel[]>([]);
  selectedAnnotation: GraphAnnotationModel | null = null;

  targets = signal<PatientTargetModel[]>([]);
  behaviors = signal<PatientBehaviorModel[]>([]);
  programs = signal<PatientProgramModel[]>([]);
  skillAreas = signal<SkillAreaModel[]>([]);

  searchKeyword = signal<string | null>(null);
  selectedFilters = signal<any[]>([]);

  includeDeleted = signal<boolean>(false);

  showAddAnnotation = signal<boolean>(false);
  
  possibleFilters = computed<SelectItemGroup[]>(() => {
    return [
      {
        label: 'Attributes',
        items: [
          {
            label: 'Is Deleted',
            value: 'IsDeleted',
            icon: 'pi pi-trash',
          },
        ],
      },
    ];
  });

  filteredAnnotations = computed<GraphAnnotationModel[]>(() => {
    let filteredAnnotations = this.annotations();
    if (this.searchKeyword() != null && this.searchKeyword() != '') {
      filteredAnnotations = filteredAnnotations.filter((annotation) => annotation.Label?.toLowerCase().includes(this.searchKeyword()!.toLowerCase()));
    }
    if (!this.includeDeleted()) {
      filteredAnnotations = filteredAnnotations.filter((annotation) => !annotation.DeletedDate);
    }
    if (this.selectedFilters() == null) {
      return filteredAnnotations;
    }
    return filteredAnnotations;
  });
  
  constructor(
    private graphApiService: GraphApiService,
    private supervisorApiService: SupervisorApiService,
    private staffApiService: StaffApiService,
    private router: Router,
    private alertService: AlertService,
    private accountApiService: AccountApiService,
    private patientDataFacade: PatientDataFacadeService
  ) {
    effect(() => {
      if (this.patientID()) {
        this.loadData();
      } else {
        this.router.navigate(['/select-patient']);
      }
    });
  }

  ngOnInit(): void {
    this.patientIDAsNumber = Number(this.patientID());
    this.loadData();
  }

  loadData() {
    this.isLoading = true;
    forkJoin({
      targets: this.patientDataFacade.getTargets(this.patientIDAsNumber!, {
        onlyActive: false,
      }),
      behaviors: this.staffApiService.GetPatientBehaviors({ patientID: this.patientIDAsNumber! }),
      programs: this.patientDataFacade.getPrograms(this.patientIDAsNumber!, {
        onlyActive: false,
      }),
      skillAreas: this.supervisorApiService.GetSkillAreas({ patientID: this.patientIDAsNumber! }),
      annotations: this.graphApiService.GetGraphAnnotations({ patientID: this.patientIDAsNumber! }),
      user: this.accountApiService.Me()
    }).subscribe({
      next: (result) => {
        this.targets.set(result.targets);
        this.behaviors.set(result.behaviors);
        this.programs.set(result.programs);
        this.skillAreas.set(result.skillAreas);
        this.annotations.set(result.annotations);
        this.user.set(result.user);
      },
      complete: () => {
        this.isLoading = false;
      },
      error: (error) => {
        console.error(error);
        this.alertService.error('Error loading data for multi-selects');
        this.isLoading = false;
      }
    });
  }

  addAnnotationButtonClicked() {
    this.showAddAnnotation.set(true);
  }

  canManageAnnotations(): boolean {
    return this.user()?.Permissions?.includes(Permissions.SparkApp_Supervisor) ?? false;
  }

  clearSearch() {
    this.searchKeyword.set(null);
    this.includeDeleted.set(false);
  }
  
  toggleIncludeDeleted() {
    this.includeDeleted.set(!this.includeDeleted());
  }
  
  onCloseAddAnnotation(withChanges: boolean = false) {
    console.log('onCloseAddAnnotation', withChanges);
    if (withChanges) {
      this.loadData();
    }
    this.showAddAnnotation.set(false);
  }

  onDeleteAnnotation() {
    this.loadData();
  }

  onEditAnnotationClicked(annotation: GraphAnnotationModel) {
    console.log('onEditAnnotationClicked', annotation);
    this.showAddAnnotation.set(true);
    this.selectedAnnotation = annotation;
  }

}
